import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  makeStyles, shorthands, tokens, Title3, Text, Divider, Dropdown, Option, Switch, SwitchOnChangeData,
  Button, Input, Spinner,
  Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from "@fluentui/react-components";
import { ChevronDown24Regular, ChevronRight24Regular } from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import { environment } from "../../config/environment";
import { UserRole, ROLE_HIERARCHY, canHoldRole, normaliseBackendRoleToUserRole } from "../../types/UserRole";
import {
  getTabColouringEnabled,
  setTabColouringEnabled,
  overrideSpreadsheetId,
  clearSpreadsheetMetadata,
} from "../../services/spreadsheet-metadata.service";
import { getDiscussionsBySpreadsheet } from "../../services/discussions.service";
import { getStatusItemsBySpreadsheet } from "../../services/status-items.service";
import { useMaxRole } from "../../hooks/useMaxRole";
import { useTypographyStyles } from "../../styles/typography.styles";

/**
 * User preferences — role override, tab-colouring toggle, and environment info.
 */

const useStyles = makeStyles({
  root: {
    ...shorthands.padding(tokens.spacingVerticalXL),
    // `100vh` was the source of a phantom scrollbar — the padding pushed the
    // element above the viewport height. Sizing to the parent with
    // border-box accounting keeps the page within bounds.
    height: "100%",
    boxSizing: "border-box",
    overflow: "auto",
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    marginTop: tokens.spacingVerticalXL,
    marginBottom: tokens.spacingVerticalXXL,
  },
  sectionTitle: {
    display: "block",
    marginBottom: tokens.spacingVerticalM,
  },
  settingDescription: {
    display: "block",
  },
  techInfoSection: {
    marginTop: tokens.spacingVerticalXXL,
    ...shorthands.padding(tokens.spacingVerticalM),
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: `0 0 0 1px ${tokens.colorNeutralStroke2}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  // Clickable header row that toggles the body's visibility. Padding-free so
  // it sits flush with the section's existing padding.
  techInfoHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    cursor: "pointer",
    userSelect: "none",
    background: "transparent",
    border: "none",
    padding: 0,
    textAlign: "left",
  },
  techInfoText: {
    // Layout-only marker; typography classes drive the actual text styling.
  },
  dropdown: {
    display: "block",
    maxWidth: "300px",
    marginTop: tokens.spacingVerticalS,
  },
  settingRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: tokens.spacingVerticalS,
  },
  settingLabel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  // Danger-zone sub-section inside Technical Information. Yellow-ish tint so
  // it visually reads as "power-user territory" without shouting.
  debugSection: {
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  debugHeading: {
    color: tokens.colorPaletteYellowForeground2,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  debugRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
  debugRowStacked: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  debugInput: {
    flex: 1,
  },
  dialogSpinner: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalM,
  },
  dialogCounts: {
    marginTop: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  dialogError: {
    color: tokens.colorPaletteRedForeground1,
    display: "block",
    marginTop: tokens.spacingVerticalS,
  },
});

const Settings: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { spreadsheetId, userRole, setUserRole, userInfo, isLoading } = useWorkflowIntelligence();
  const [tabColouringEnabled, setTabColouringEnabledState] = useState(true);
  const [isLoadingTabColourSetting, setIsLoadingTabColourSetting] = useState(true);
  // The user's Digital Hub-derived maximum role — filters the role picker
  // below so users can't promote themselves above what their backend role
  // permits. Previously this was a `useState(null)` that nothing ever set,
  // which silently unlocked Partner in the dropdown for everyone.
  const maxRole = useMaxRole();
  // Technical Information section is hidden by default — opens on click. Users
  // shouldn't see version / API URL / spreadsheet ID unless they're looking
  // for them (e.g. when filing a support ticket).
  const [isTechInfoExpanded, setIsTechInfoExpanded] = useState(false);

  // Debug controls (Override / Unlink). Both open a confirmation dialog that
  // fires two count queries in parallel so the user sees what would be
  // affected before committing. Reload-page on success so the whole app
  // repopulates against the new (or now-absent) spreadsheet ID.
  const [overrideIdInput, setOverrideIdInput] = useState("");
  const [pendingAction, setPendingAction] = useState<"override" | "unlink" | null>(null);
  const [counts, setCounts] = useState<{ discussions: number; statusItems: number } | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [countsError, setCountsError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);
  const GUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  useEffect(() => {
    setOverrideIdInput(spreadsheetId ?? "");
  }, [spreadsheetId]);

  const openConfirmDialog = async (action: "override" | "unlink") => {
    setPendingAction(action);
    setCounts(null);
    setCountsError(null);
    if (!spreadsheetId) {
      // No current ID — no impact preview possible. Just open the dialog
      // with zero counts so the user still sees a confirmation gate.
      setCounts({ discussions: 0, statusItems: 0 });
      return;
    }
    setCountsLoading(true);
    try {
      const [discussions, statusItems] = await Promise.all([
        getDiscussionsBySpreadsheet(spreadsheetId),
        getStatusItemsBySpreadsheet(spreadsheetId),
      ]);
      setCounts({ discussions: discussions.length, statusItems: statusItems.length });
    } catch (err) {
      setCountsError((err as Error).message ?? "Failed to load counts.");
    } finally {
      setCountsLoading(false);
    }
  };

  const closeDialog = () => {
    if (isActing) return;
    setPendingAction(null);
    setCounts(null);
    setCountsError(null);
  };

  const runOverride = async () => {
    const id = overrideIdInput.trim();
    if (!GUID_PATTERN.test(id)) return;
    setIsActing(true);
    try {
      await overrideSpreadsheetId(id);
      window.location.reload();
    } catch (err) {
      alert(`Failed to override: ${(err as Error).message}`);
      setIsActing(false);
    }
  };

  const runUnlink = async () => {
    setIsActing(true);
    try {
      await clearSpreadsheetMetadata();
      window.location.reload();
    } catch (err) {
      alert(`Failed to unlink: ${(err as Error).message}`);
      setIsActing(false);
    }
  };

  /**
   * Normalise the backend's max-role string into our enum. Two special
   * cases:
   *   - "Manager" is not a distinct tier in the addin; treat it as
   *     Reviewer (Managers get the same permissions as Reviewers).
   *   - Any other unknown value (or null while the hook is loading)
   *     returns null → the dropdown locks down to the current selection
   *     rather than optimistically showing all roles.
   */
  // Delegates to normaliseBackendRoleToUserRole so the same rules (Manager
  // → Reviewer, unrecognised → null) apply here as in the context's role
  // auto-init.
  const normalisedMaxRole: UserRole | null = useMemo(
    () => normaliseBackendRoleToUserRole(maxRole),
    [maxRole],
  );

  /**
   * The roles this user is allowed to pick. Empty until we've resolved a
   * concrete max role — the dropdown falls back to "locked to current"
   * in that case, which is the security-safe default (previously we
   * defaulted to "show all roles including Partner" during the async
   * load window, which is how the Partner-visible-to-everyone bug landed).
   */
  const allowedRoles: UserRole[] = useMemo(() => {
    if (!normalisedMaxRole) return [];
    const maxIndex = ROLE_HIERARCHY.indexOf(normalisedMaxRole);
    return ROLE_HIERARCHY.slice(0, maxIndex + 1);
  }, [normalisedMaxRole]);

  // Cap the current role if maxRole says it's too high. Runs whenever max
  // role finishes loading (or changes via userInfo change). Skipped when
  // we couldn't normalise the backend value — better to leave the user's
  // picked role alone than to silently downgrade based on a value we
  // don't recognise.
  useEffect(() => {
    if (!normalisedMaxRole) return;
    if (!canHoldRole(userRole, normalisedMaxRole)) {
      setUserRole(normalisedMaxRole);
    }
  }, [normalisedMaxRole, userRole, setUserRole]);

  // Load the tab colouring setting on mount
  useEffect(() => {
    const loadSetting = async () => {
      try {
        const enabled = await getTabColouringEnabled();
        setTabColouringEnabledState(enabled);
      } catch (error) {
        console.error("Failed to load tab colouring setting:", error);
      } finally {
        setIsLoadingTabColourSetting(false);
      }
    };
    loadSetting();
  }, []);

  const handleTabColouringChange = async (checked: boolean) => {
    setTabColouringEnabledState(checked);
    try {
      await setTabColouringEnabled(checked);
    } catch (error) {
      console.error("Failed to save tab colouring setting:", error);
      // Revert on error
      setTabColouringEnabledState(!checked);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Title3>Settings</Title3>
      </div>

      <Divider />

      <div className={styles.section}>
        <Text className={`${typography.pageTitle} ${styles.sectionTitle}`}>Worksheet</Text>
        <div className={styles.settingRow}>
          <div className={styles.settingLabel}>
            <Text>Automatic tab colouring</Text>
            <Text className={`${typography.caption} ${styles.settingDescription}`}>
              Colour worksheet tabs based on their status
            </Text>
          </div>
          <Switch
            checked={tabColouringEnabled}
            disabled={isLoadingTabColourSetting}
            onChange={(_, data) => handleTabColouringChange(data.checked)}
          />
        </div>
      </div>

      <Divider />

      <div className={styles.section}>
        <Text className={`${typography.pageTitle} ${styles.sectionTitle}`}>User Role</Text>
        <Text as="p" className={`${typography.caption} ${styles.settingDescription}`}>
          Select your role for workflow purposes
        </Text>
        <Dropdown
          className={styles.dropdown}
          value={userRole}
          selectedOptions={[userRole]}
          // Disabled while the max role hasn't been resolved (or the
          // backend returned a value we don't recognise) — locks the
          // user to whatever they currently have picked instead of
          // opening the dropdown up. Renders one option (the current
          // role) so the label still shows.
          disabled={allowedRoles.length === 0}
          onOptionSelect={(_, data) => {
            if (data.optionValue) {
              setUserRole(data.optionValue as UserRole);
            }
          }}
        >
          {(allowedRoles.length === 0 ? [userRole] : allowedRoles).map((role) => (
            <Option key={role} value={role}>
              {role}
            </Option>
          ))}
        </Dropdown>
      </div>

      <Divider />

      <div className={styles.techInfoSection}>
        <button
          type="button"
          className={styles.techInfoHeader}
          onClick={() => setIsTechInfoExpanded((v) => !v)}
          aria-expanded={isTechInfoExpanded ? "true" : "false"}
        >
          {isTechInfoExpanded ? <ChevronDown24Regular /> : <ChevronRight24Regular />}
          <Text className={typography.sectionLabel}>Technical Information</Text>
        </button>
        {isTechInfoExpanded && (
          <>
            <Text className={`${typography.monospace} ${styles.techInfoText}`}>
              Version: {environment.version}m
            </Text>
            <Text className={`${typography.monospace} ${styles.techInfoText}`}>
              Environment: {environment.production ? 'Production' : environment.name === 'dev' ? 'Development' : 'Local'}
            </Text>
            <Text className={`${typography.monospace} ${styles.techInfoText}`}>
              API: {environment.api.baseUrl}
            </Text>
            <Text className={`${typography.monospace} ${styles.techInfoText}`}>
              Spreadsheet ID: {isLoading ? "Loading..." : (spreadsheetId || "Not available")}
            </Text>

            <div className={styles.debugSection}>
              <Text className={styles.debugHeading}>Debug controls</Text>

              {/* Override — repoint at a specific spreadsheet ID */}
              <div className={styles.debugRowStacked}>
                <Text className={typography.caption}>
                  Override spreadsheet ID. Repoints this workbook at a different Workflow Intelligence record.
                </Text>
                <div className={styles.debugRow}>
                  <Input
                    value={overrideIdInput}
                    onChange={(_, data) => setOverrideIdInput(data.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    className={styles.debugInput}
                  />
                  <Button
                    onClick={() => openConfirmDialog("override")}
                    disabled={
                      !GUID_PATTERN.test(overrideIdInput.trim()) ||
                      overrideIdInput.trim() === spreadsheetId
                    }
                  >
                    Apply
                  </Button>
                </div>
              </div>

              {/* Unlink — clear metadata entirely */}
              <div className={styles.debugRowStacked}>
                <Text className={typography.caption}>
                  Unlink workbook from Workflow Intelligence. Next open will create a fresh record.
                </Text>
                <Button
                  appearance="secondary"
                  onClick={() => openConfirmDialog("unlink")}
                  disabled={!spreadsheetId}
                >
                  Unlink
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog
        open={pendingAction !== null}
        onOpenChange={(_, data) => { if (!data.open) closeDialog(); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {pendingAction === "override" ? "Override spreadsheet ID?" : "Unlink workbook?"}
            </DialogTitle>
            <DialogContent>
              <Text>
                {pendingAction === "override"
                  ? "This workbook will point at a different Workflow Intelligence record. The addin will reload."
                  : "This workbook will no longer be tracked in Workflow Intelligence. Next time it's opened, a fresh record will be created. The addin will reload."}
              </Text>

              {countsLoading && (
                <div className={styles.dialogSpinner}>
                  <Spinner size="tiny" label="Checking impact..." />
                </div>
              )}

              {countsError && (
                <Text className={styles.dialogError}>
                  Couldn't load impact preview: {countsError}. You can still proceed.
                </Text>
              )}

              {counts && !countsLoading && (
                <div className={styles.dialogCounts}>
                  <Text className={typography.bodySmall}>
                    Current record has <strong>{counts.discussions}</strong> discussion{counts.discussions === 1 ? "" : "s"}
                    {" "}and <strong>{counts.statusItems}</strong> status item{counts.statusItems === 1 ? "" : "s"}.
                    They will remain in the database but be unreachable from this workbook.
                  </Text>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={isActing}>Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                disabled={isActing || countsLoading}
                onClick={() => pendingAction === "override" ? runOverride() : runUnlink()}
              >
                {isActing ? "Working..." : (pendingAction === "override" ? "Override" : "Unlink")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default Settings;
